import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, PageHeader, Alert, ProgressBar } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const S_COLOR = { valid:'success', invalid:'danger', risky:'warning', unknown:'gray' };
const S_ICON  = { valid:'✅', invalid:'❌', risky:'⚠️', unknown:'❓' };

export default function Verifier() {
  const { user } = useAuthStore();
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [history, setHistory] = useState([]);
  const [bulkText, setBulkText] = useState('');
  const [tab, setTab] = useState('single');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const fileRef = useRef();
  // History filters
  const [filterStatus, setFilterStatus]       = useState('all');
  const [filterDisposable, setFilterDisposable] = useState(false);
  const [filterRole, setFilterRole]           = useState(false);
  const [filterCatchAll, setFilterCatchAll]   = useState(false);
  const [filterFree, setFilterFree]           = useState(false);
  const [filterSearch, setFilterSearch]       = useState('');

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try { const r = await api.get('/verifier/history'); setHistory(r.data.results || []); } catch {}
  };

  const verifySingle = async () => {
    if (!email) return toast.error('Enter an email address');
    setChecking(true); setResult(null);
    try { const r = await api.post('/verifier/single', { email, level:7 }); setResult(r.data.result); fetchHistory(); }
    catch (e) { toast.error(e.response?.data?.message || 'Verification failed'); } finally { setChecking(false); }
  };

  const verifyBulkText = async () => {
    const emails = [...new Set(bulkText.split(/[\n,;]/).map(e => e.trim()).filter(Boolean))];
    if (!emails.length) return toast.error('Enter at least one email');
    setBulkLoading(true); setBulkResult(null);
    try { const r = await api.post('/verifier/bulk', { emails, level:7 }); setBulkResult(r.data); fetchHistory(); toast.success(`Verified ${emails.length} emails`); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setBulkLoading(false); }
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvLoading(true); setBulkResult(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/verifier/bulk-csv', fd);
      setBulkResult(r.data);
      toast.success(`Verified ${r.data.summary.processed} emails from CSV!`);
      fetchHistory();
    } catch (e) { toast.error(e.response?.data?.message || 'CSV upload failed'); }
    finally { setCsvLoading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const downloadBatch = async (batchId) => {
    try {
      const r = await api.get(`/verifier/export/${batchId}`, { responseType:'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `verification-${batchId.slice(0,8)}.csv`; a.click();
    } catch { toast.error('Download failed'); }
  };

  const downloadAll = async () => {
    try {
      const r = await api.get('/verifier/export-all', { responseType:'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'all-verifications.csv'; a.click();
    } catch { toast.error('Download failed'); }
  };

  const creditsLeft = user?.verifyLimit === -1 ? '∞' : Math.max(0, (user?.verifyLimit||0) - (user?.verifyUsed||0));
  const pct = user?.verifyLimit > 0 ? Math.min(100, ((user?.verifyUsed||0) / user.verifyLimit) * 100) : 0;

  const batches = history.reduce((acc, r) => {
    const key = r.batchId || 'single';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r); return acc;
  }, {});

  // Apply filters to history
  const filteredHistory = history.filter(h => {
    if (filterSearch && !h.email.toLowerCase().includes(filterSearch.toLowerCase())) return false;
    if (filterStatus !== 'all' && h.status !== filterStatus) return false;
    if (filterDisposable && !h.reason?.includes('Disposable')) return false;
    if (filterRole      && !h.reason?.includes('Role-based'))  return false;
    if (filterCatchAll  && !h.reason?.includes('Catch-all'))   return false;
    if (filterFree      && !h.reason?.includes('free'))        return false;
    return true;
  });

  return (
    <div>
      <PageHeader title="Email Verifier" subtitle="7-level: Format → MX → SMTP → Disposable → Role → Free Provider → Catch-All" action={
        <Button variant="secondary" size="sm" onClick={downloadAll}>⬇ Download All History</Button>
      } />

      {/* Credits bar */}
      <Card style={{ marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:user?.verifyLimit!==-1?10:0 }}>
          <div>
            <div style={{ fontSize:11, color:'#555', fontWeight:700, textTransform:'uppercase', marginBottom:2 }}>Verification Credits</div>
            <div style={{ fontSize:22, fontWeight:800, color: pct>=90?'#E74C3C':pct>=70?'#F39C12':'#2ECC71' }}>{creditsLeft} remaining</div>
          </div>
          <div style={{ textAlign:'right', fontSize:13, color:'#555' }}>
            <div>{user?.verifyUsed||0} used</div>
            <div>{user?.verifyLimit===-1?'Unlimited':user?.verifyLimit?.toLocaleString()} total</div>
          </div>
        </div>
        {user?.verifyLimit !== -1 && <ProgressBar value={user?.verifyUsed||0} max={user?.verifyLimit||1} />}
        {pct >= 90 && <Alert type="warning" style={{ marginTop:8, padding:'8px 12px' }}>Verification credits almost full. Upgrade to verify more emails.</Alert>}
      </Card>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid rgba(46,204,113,0.1)' }}>
        {[['single','✉️ Single'],['bulk','📋 Bulk Text'],['csv','📂 CSV Upload'],['history','🕐 History']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding:'10px 18px', background:'none', border:'none', borderBottom:`2px solid ${tab===id?'#2ECC71':'transparent'}`, color:tab===id?'#2ECC71':'#666', fontWeight:tab===id?700:400, fontSize:13, cursor:'pointer', marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── SINGLE ── */}
      {tab==='single' && (
        <div style={{ maxWidth:580 }}>
          <Card>
            <h3 style={{ fontWeight:700, marginBottom:16 }}>Verify Single Email</h3>
            <div style={{ display:'flex', gap:10 }}>
              <input placeholder="Enter email address..." value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key==='Enter' && verifySingle()}
                style={{ flex:1, padding:'11px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:14, outline:'none' }} />
              <Button loading={checking} onClick={verifySingle}>Verify</Button>
            </div>

            {result && (
              <div style={{ marginTop:20, background:'#1a1a1a', borderRadius:12, padding:20, border:`1px solid ${result.status==='valid'?'rgba(46,204,113,0.3)':result.status==='risky'?'rgba(243,156,18,0.3)':'rgba(231,76,60,0.3)'}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                  <span style={{ fontSize:32 }}>{S_ICON[result.status]}</span>
                  <div>
                    <div style={{ fontWeight:800, fontSize:15 }}>{result.email}</div>
                    <Badge color={S_COLOR[result.status]}>{result.status?.toUpperCase()}</Badge>
                  </div>
                </div>

                {/* Core 3 checks */}
                <div style={{ fontSize:11, color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Core Checks</div>
                {[
                  ['✉️ Format',        result.formatValid],
                  ['🌐 MX / DNS',      result.mxValid],
                  ['📡 SMTP Ping',     result.smtpValid],
                ].map(([l,v]) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:13 }}>
                    <span style={{ color:'#888' }}>{l}</span>
                    <span style={{ color:v?'#2ECC71':'#E74C3C', fontWeight:700 }}>{v?'✓ Pass':'✗ Fail'}</span>
                  </div>
                ))}

                {/* Advanced 4 checks */}
                <div style={{ fontSize:11, color:'#555', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginTop:14, marginBottom:8 }}>Advanced Checks</div>
                {[
                  ['🗑️ Disposable',    result.isDisposable,   true],
                  ['👤 Role-Based',    result.isRoleBased,    true],
                  ['🆓 Free Provider', result.isFreeProvider, false],
                  ['🪤 Catch-All',     result.isCatchAll,     true],
                ].map(([l, v, warnIfTrue]) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:13 }}>
                    <span style={{ color:'#888' }}>{l}</span>
                    <span style={{ color: v === undefined ? '#555' : warnIfTrue ? (v ? '#E74C3C' : '#2ECC71') : (v ? '#F39C12' : '#2ECC71'), fontWeight:700 }}>
                      {v === undefined ? '—' : warnIfTrue ? (v ? '⚠️ Yes' : '✓ No') : (v ? '📧 Yes' : '✓ No')}
                    </span>
                  </div>
                ))}

                {result.reason && (
                  <div style={{ marginTop:12, padding:'10px 14px', background:'rgba(255,255,255,0.03)', borderRadius:8, fontSize:12, color:'#888', fontStyle:'italic' }}>
                    💬 {result.reason}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── BULK TEXT ── */}
      {tab==='bulk' && (
        <div style={{ maxWidth:640 }}>
          <Card>
            <h3 style={{ fontWeight:700, marginBottom:8 }}>Bulk Verify — Paste Emails</h3>
            <p style={{ fontSize:13, color:'#555', marginBottom:16 }}>Paste emails separated by newline, comma, or semicolon. Max 1,000 per batch.</p>
            <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} rows={8} placeholder={'email1@company.com\nemail2@company.com\nemail3@company.com'}
              style={{ width:'100%', padding:'12px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none', resize:'vertical', fontFamily:'monospace' }} />
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12 }}>
              <span style={{ fontSize:12, color:'#555' }}>{[...new Set(bulkText.split(/[\n,;]/).filter(e=>e.trim()))].length} emails</span>
              <div style={{ display:'flex', gap:8 }}>
                {bulkResult && <Button size="sm" variant="secondary" onClick={() => downloadBatch(bulkResult.batchId)}>⬇ Download CSV</Button>}
                <Button loading={bulkLoading} onClick={verifyBulkText}>Verify All</Button>
              </div>
            </div>
            {bulkResult && <BulkSummary summary={bulkResult.summary} />}
          </Card>
        </div>
      )}

      {/* ── CSV UPLOAD ── */}
      {tab==='csv' && (
        <div style={{ maxWidth:640 }}>
          <Card>
            <h3 style={{ fontWeight:700, marginBottom:8 }}>📂 Upload CSV to Verify</h3>
            <p style={{ fontSize:13, color:'#555', marginBottom:16 }}>Upload any CSV — the email column is auto-detected regardless of its header name. Max 5MB. Results downloadable as CSV.</p>
            <div style={{ border:'2px dashed rgba(46,204,113,0.25)', borderRadius:14, padding:'36px', textAlign:'center', cursor:'pointer', transition:'all 0.2s', marginBottom:16 }}
              onClick={() => fileRef.current?.click()}
              onMouseEnter={e => e.currentTarget.style.borderColor='rgba(46,204,113,0.5)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='rgba(46,204,113,0.25)'}>
              <div style={{ fontSize:40, marginBottom:8 }}>{csvLoading?'⏳':'📂'}</div>
              <div style={{ fontSize:14, fontWeight:700, color:'#ccc', marginBottom:4 }}>{csvLoading?'Verifying emails...':'Click to upload CSV'}</div>
              <div style={{ fontSize:12, color:'#555' }}>Any CSV accepted — email column is auto-detected</div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={handleCSV} />
            </div>
            {bulkResult && (
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                <Button variant="secondary" onClick={() => downloadBatch(bulkResult.batchId)}>⬇ Download Results CSV</Button>
              </div>
            )}
            {bulkResult && <BulkSummary summary={bulkResult.summary} />}
          </Card>
        </div>
      )}

      {/* ── HISTORY ── */}
      {tab==='history' && (
        <Card style={{ padding:0, overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px', borderBottom:'1px solid rgba(46,204,113,0.08)' }}>
            <span style={{ fontWeight:700 }}>Verification History</span>
            <Button size="sm" variant="secondary" onClick={downloadAll}>⬇ Download All CSV</Button>
          </div>
          {/* ── FILTERS ── */}
          <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(46,204,113,0.08)', display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            {/* Search */}
            <input placeholder="🔍 Search email..." value={filterSearch} onChange={e=>setFilterSearch(e.target.value)}
              style={{ padding:'7px 12px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:8, color:'#fff', fontSize:13, outline:'none', width:200 }} />
            {/* Status filter */}
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
              style={{ padding:'7px 12px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:8, color:'#fff', fontSize:13, outline:'none' }}>
              <option value="all">All Status</option>
              <option value="valid">✅ Valid</option>
              <option value="risky">⚠️ Risky</option>
              <option value="invalid">❌ Invalid</option>
            </select>
            {/* Toggle filters */}
            {[
              ['🗑️ Disposable', filterDisposable, setFilterDisposable, '#E74C3C'],
              ['👤 Role-Based',  filterRole,       setFilterRole,       '#F39C12'],
              ['🪤 Catch-All',   filterCatchAll,   setFilterCatchAll,   '#F39C12'],
              ['🆓 Free',        filterFree,        setFilterFree,       '#3498DB'],
            ].map(([label, active, setter, color]) => (
              <button key={label} onClick={() => setter(p=>!p)}
                style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${active?color:'rgba(255,255,255,0.1)'}`, background:active?`${color}22`:'transparent', color:active?color:'#666', fontSize:12, fontWeight:active?700:400, cursor:'pointer' }}>
                {label}
              </button>
            ))}
            {/* Reset */}
            {(filterSearch||filterStatus!=='all'||filterDisposable||filterRole||filterCatchAll||filterFree) && (
              <button onClick={()=>{ setFilterSearch(''); setFilterStatus('all'); setFilterDisposable(false); setFilterRole(false); setFilterCatchAll(false); setFilterFree(false); }}
                style={{ padding:'7px 14px', borderRadius:8, border:'1px solid rgba(231,76,60,0.3)', background:'rgba(231,76,60,0.08)', color:'#E74C3C', fontSize:12, cursor:'pointer' }}>
                ✕ Reset
              </button>
            )}
            <span style={{ fontSize:12, color:'#555', marginLeft:'auto' }}>{filteredHistory.length} of {history.length} results</span>
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'rgba(46,204,113,0.05)', borderBottom:'1px solid rgba(46,204,113,0.1)' }}>
                  {['Email','Format','MX','SMTP','Disposable','Role','Catch-All','Status','Reason','Date','⬇'].map(h => (
                    <th key={h} style={{ padding:'11px 12px', textAlign:'left', color:'#555', fontWeight:700, fontSize:11, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length===0
                  ? <tr><td colSpan={11} style={{ textAlign:'center', padding:32, color:'#555' }}>No history yet</td></tr>
                  : filteredHistory.slice(0,200).map(h => (
                    <tr key={h.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                      <td style={{ padding:'9px 12px', color:'#2ECC71', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.email}</td>
                      <td style={{ padding:'9px 12px', color:h.formatValid?'#2ECC71':'#E74C3C', textAlign:'center' }}>{h.formatValid?'✓':'✗'}</td>
                      <td style={{ padding:'9px 12px', color:h.mxValid?'#2ECC71':'#E74C3C', textAlign:'center' }}>{h.mxValid?'✓':'✗'}</td>
                      <td style={{ padding:'9px 12px', color:h.smtpValid?'#2ECC71':'#E74C3C', textAlign:'center' }}>{h.smtpValid?'✓':'✗'}</td>
                      <td style={{ padding:'9px 12px', textAlign:'center' }}>
                        {h.reason?.includes('Disposable') ? <span style={{ color:'#E74C3C' }}>⚠️</span> : <span style={{ color:'#2ECC71' }}>✓</span>}
                      </td>
                      <td style={{ padding:'9px 12px', textAlign:'center' }}>
                        {h.reason?.includes('Role-based') ? <span style={{ color:'#F39C12' }}>⚠️</span> : <span style={{ color:'#2ECC71' }}>✓</span>}
                      </td>
                      <td style={{ padding:'9px 12px', textAlign:'center' }}>
                        {h.reason?.includes('Catch-all') ? <span style={{ color:'#F39C12' }}>⚠️</span> : <span style={{ color:'#2ECC71' }}>✓</span>}
                      </td>
                      <td style={{ padding:'9px 12px' }}><Badge color={S_COLOR[h.status]}>{h.status}</Badge></td>
                      <td style={{ padding:'9px 12px', color:'#555', fontSize:11, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.reason||'—'}</td>
                      <td style={{ padding:'9px 12px', color:'#555', fontSize:11, whiteSpace:'nowrap' }}>{h.createdAt ? new Date(h.createdAt).toLocaleDateString():''}</td>
                      <td style={{ padding:'9px 12px' }}>{h.batchId && <Button size="sm" variant="ghost" onClick={() => downloadBatch(h.batchId)}>⬇</Button>}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function BulkSummary({ summary }) {
  return (
    <div style={{ marginTop:16 }}>
      {/* Main stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:10 }}>
        {[
          ['Total',    summary?.total||summary?.processed, '#fff'],
          ['✅ Valid',  summary?.valid,                    '#2ECC71'],
          ['⚠️ Risky',  summary?.risky,                    '#F39C12'],
          ['❌ Invalid',summary?.invalid,                   '#E74C3C'],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background:'#1a1a1a', borderRadius:10, padding:'12px', textAlign:'center' }}>
            <div style={{ fontSize:22, fontWeight:800, color:c }}>{v||0}</div>
            <div style={{ fontSize:11, color:'#555', marginTop:2 }}>{l}</div>
          </div>
        ))}
      </div>
      {/* Advanced stats */}
      {(summary?.disposable > 0 || summary?.roleBased > 0 || summary?.catchAll > 0) && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
          {[
            ['🗑️ Disposable', summary?.disposable, '#E74C3C'],
            ['👤 Role-Based', summary?.roleBased,  '#F39C12'],
            ['🪤 Catch-All',  summary?.catchAll,   '#F39C12'],
          ].map(([l,v,c]) => (
            <div key={l} style={{ background:'#1a1a1a', borderRadius:10, padding:'10px', textAlign:'center', border:`1px solid ${c}22` }}>
              <div style={{ fontSize:18, fontWeight:800, color:c }}>{v||0}</div>
              <div style={{ fontSize:11, color:'#555', marginTop:2 }}>{l}</div>
            </div>
          ))}
        </div>
      )}
      {summary?.skipped > 0 && (
        <div style={{ marginTop:8, fontSize:12, color:'#555', textAlign:'center' }}>
          ⚡ {summary.skipped} emails skipped (credit limit reached)
        </div>
      )}
    </div>
  );
}
